import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@repo/components/src/ui/avatar";
import { Box } from "@repo/components/src/ui/box";
import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import { Card } from "@repo/components/src/ui/card";
import { HStack } from "@repo/components/src/ui/hstack";
import { Image } from "@repo/components/src/ui/image";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { CampaignAnalyticFragmentFragment } from "@repo/graphql/documents/campaign/fragment.generated";
import { Download } from "lucide-react-native";
import React from "react";
import { Linking } from "react-native";
import { formatDateShort } from "../shared/utils";

type WorkOrder = CampaignAnalyticFragmentFragment["workOrders"][number];
type Detail = WorkOrder["details"][number];

export type ClipItem = {
  id: string;
  workOrder: WorkOrder;
  detail: Detail;
};

type Props = {
  items: ClipItem[];
  rightOfUseMonths: number | null | undefined;
};

const Clips = React.memo(({ items, rightOfUseMonths }: Props) => {
  if (!items.length) {
    return (
      <Text size="sm" className="text-typography-500">
        Aún no hay clips entregados en esta campaña.
      </Text>
    );
  }
  return (
    <HStack className="flex-wrap -m-2">
      {items.map((item) => (
        <Box key={item.id} className="w-1/4 p-2">
          <ClipCard item={item} rightOfUseMonths={rightOfUseMonths} />
        </Box>
      ))}
    </HStack>
  );
});

Clips.displayName = "UgcClips";

const rightsLabel = (months: number | null | undefined) => {
  if (!months) return "Sin derechos de uso";
  if (months === 1) return "Derechos de uso · 1 mes";
  return `Derechos de uso · ${months} meses`;
};

const ClipCard = ({
  item,
  rightOfUseMonths,
}: {
  item: ClipItem;
  rightOfUseMonths: number | null | undefined;
}) => {
  const { workOrder, detail } = item;
  const thumbnail = detail.thumbnail;
  const contentFile = detail.contentFile;
  const avatar = workOrder.avatar;
  const approvedAt = formatDateShort(detail.approvedAt);

  const handleDownload = () => {
    const url = contentFile?.url;
    if (!url) return;
    if (typeof document !== "undefined") {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener";
      link.download = "";
      link.click();
      return;
    }
    Linking.openURL(url);
  };

  return (
    <Card
      variant="outline"
      size="xs"
      className="rounded-2xl overflow-hidden h-full p-0"
    >
      <Box className="bg-typography-900 aspect-[9/12] w-full">
        {contentFile?.url ? (
          <video
            src={contentFile.url}
            poster={thumbnail?.url}
            controls
            preload="metadata"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : thumbnail?.url ? (
          <Image
            source={{ uri: thumbnail.url }}
            alt="thumbnail"
            size="none"
            className="w-full h-full"
            contentFit="cover"
            contentPosition="center"
          />
        ) : null}
      </Box>
      <VStack className="px-3 py-3" space="sm">
        <HStack className="items-center" space="xs">
          <Avatar size="xs">
            <AvatarFallbackText>{workOrder.fullName}</AvatarFallbackText>
            <AvatarImage source={{ uri: avatar?.url }} />
          </Avatar>
          <Text
            size="sm"
            className="text-typography-900 font-semibold flex-1"
            numberOfLines={1}
          >
            {workOrder.fullName}
          </Text>
          {approvedAt ? (
            <Text size="xs" className="text-typography-500">
              {approvedAt}
            </Text>
          ) : null}
        </HStack>
        <Text size="xs" className="text-typography-500">
          {rightsLabel(rightOfUseMonths)}
        </Text>
        <Button
          action="brand"
          variant="outline"
          size="sm"
          onPress={handleDownload}
          isDisabled={!contentFile?.url}
        >
          <ButtonIcon as={Download} />
          <ButtonText>Descargar</ButtonText>
        </Button>
      </VStack>
    </Card>
  );
};

export default Clips;
